import { Container, Graphics, type DestroyOptions } from 'pixi.js';
import { COLORS, FONT_FAMILY, RADIUS, BORDER } from '../app/theme';
import { getOverlayCanvas } from './domOverlay';
import { isEffectivelyVisible } from './accessibility';

export interface TextAreaOpts {
  width: number;
  height?: number;
  placeholder?: string;
  value?: string;
  align?: 'left' | 'center' | 'right';
  maxLength?: number;
  accessibilityLabel?: string;
  enterKeyHint?: 'enter' | 'done' | 'go' | 'next' | 'previous' | 'search' | 'send';
}

/** Pack an RGB number to a CSS hex string. */
function hex(c: number): string {
  return `#${c.toString(16).padStart(6, '0')}`;
}

/**
 * A multi-line text input. `@pixi/ui`'s Input is single-line only, so this overlays a real DOM
 * `<textarea>` (Persian IME + word-wrap for free) on top of the canvas, tracking the letterboxed
 * world transform each frame. The Pixi-side rounded rect is a fallback look behind the DOM node.
 * The DOM node is removed on destroy() — which SceneManager calls with {children:true} on scene exit.
 */
export class TextArea extends Container {
  readonly el: HTMLTextAreaElement;
  private readonly w: number;
  private readonly h: number;
  private readonly baseFont: number;
  private raf = 0;
  private gone = false;
  private readonly dismissOnDone = (event: KeyboardEvent): void => {
    if (event.key !== 'Enter' || event.shiftKey || event.isComposing) return;
    event.preventDefault();
    this.el.blur();
  };

  constructor(opts: TextAreaOpts) {
    super();
    this.w = opts.width;
    this.h = opts.height ?? 160;
    this.baseFont = 24;

    const bg = new Graphics()
      .roundRect(0, 0, this.w, this.h, RADIUS.field)
      .fill({ color: COLORS.inputBg })
      .stroke({ width: BORDER.thin, color: COLORS.fieldBorder });
    this.addChild(bg);

    const el = document.createElement('textarea');
    el.value = opts.value ?? '';
    el.placeholder = opts.placeholder ?? '';
    el.setAttribute('aria-label', opts.accessibilityLabel ?? opts.placeholder ?? 'ورودی متن');
    if (opts.maxLength && opts.maxLength > 0) el.maxLength = opts.maxLength;
    el.dir = 'rtl';
    el.spellcheck = false;
    // Mobile multiline keyboards normally expose only a newline key. Ask for a Done key instead;
    // Shift+Enter is still available when a deliberate line break is needed.
    el.enterKeyHint = opts.enterKeyHint ?? 'done';
    el.style.position = 'fixed';
    el.style.display = 'none';
    el.style.margin = '0';
    el.style.boxSizing = 'border-box';
    el.style.resize = 'none';
    el.style.border = `${BORDER.thin}px solid ${hex(COLORS.fieldBorder)}`;
    el.style.borderRadius = `${RADIUS.field}px`;
    el.style.background = hex(COLORS.inputBg);
    el.style.color = hex(COLORS.text);
    el.style.fontFamily = FONT_FAMILY;
    el.style.outline = 'none';
    el.style.padding = '10px 14px';
    el.style.textAlign = opts.align ?? 'right';
    el.style.zIndex = '50';
    document.body.appendChild(el);
    this.el = el;
    el.addEventListener('keydown', this.dismissOnDone);

    const tick = (): void => {
      if (this.gone) return;
      this.reposition();
      this.raf = requestAnimationFrame(tick);
    };
    this.raf = requestAnimationFrame(tick);
  }

  /** Map this widget's design-space rect to viewport pixels and size the DOM node to match. */
  private reposition(): void {
    const canvas = getOverlayCanvas();
    if (!canvas || !this.parent || !isEffectivelyVisible(this)) {
      this.el.style.display = 'none';
      return;
    }
    const origin = this.toGlobal({ x: 0, y: 0 });
    const right = this.toGlobal({ x: this.w, y: 0 });
    const scale = (right.x - origin.x) / this.w || 1;
    const rect = canvas.getBoundingClientRect();
    this.el.style.display = 'block';
    this.el.style.left = `${rect.left + origin.x}px`;
    this.el.style.top = `${rect.top + origin.y}px`;
    this.el.style.width = `${this.w * scale}px`;
    this.el.style.height = `${this.h * scale}px`;
    // iOS automatically zooms the page when a focused form control computes below 16 CSS pixels.
    this.el.style.fontSize = `${Math.max(16, this.baseFont * scale)}px`;
  }

  get value(): string {
    return this.el.value;
  }
  set value(v: string) {
    this.el.value = v;
  }

  onChange(fn: (text: string) => void): void {
    this.el.addEventListener('input', () => fn(this.el.value));
  }

  focus(): void {
    this.el.focus();
  }

  blur(): void {
    this.el.blur();
  }

  setReadonly(readonly: boolean): void {
    this.el.readOnly = readonly;
    this.alpha = readonly ? 0.6 : 1;
  }

  override destroy(options?: DestroyOptions): void {
    this.gone = true;
    cancelAnimationFrame(this.raf);
    this.el.removeEventListener('keydown', this.dismissOnDone);
    this.el.remove();
    super.destroy(options);
  }
}
